import { formatearMoneda } from '@barbones/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, BellRing, CalendarCheck2, HandCoins, PackageMinus, Wallet } from 'lucide-react';
import { api } from '@/api/cliente';
import { Esqueleto, Indicador, Insignia, Tarjeta, TarjetaEncabezado } from '@/components/ui/data';
import { Columnas } from '../reportes/Graficas';

interface Resumen {
  dia: string;
  ingresos: number;
  ventas: number;
  citas: { total: number; pendientes: number; enServicio: number; atendidas: number; canceladas: number };
  porCobrar: { ventas: number; total: number };
  caja: 'abierto' | 'bloqueado' | 'cerrada';
  stockBajo: number;
  aprobacionesPendientes: number;
  ultimos7: { dia: string; total: number }[];
}

const diaCorto = new Intl.DateTimeFormat('es-MX', { weekday: 'short', timeZone: 'America/Mexico_City' });
const CAJA = { abierto: { texto: 'Abierta', tono: 'exito' }, bloqueado: { texto: 'Bloqueada', tono: 'peligro' }, cerrada: { texto: 'Cerrada', tono: 'neutro' } } as const;

/** Indicadores del día; se actualiza solo cada minuto y con los eventos en tiempo real. */
export function Dashboard() {
  const { data, isLoading } = useQuery({ queryKey: ['resumen-hoy'], queryFn: () => api<Resumen>('/reportes/resumen/hoy'), refetchInterval: 60_000 });

  if (isLoading || !data) {
    return (
      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Esqueleto key={i} className="h-[104px]" />
        ))}
      </div>
    );
  }
  const alertas = [
    data.caja === 'bloqueado' && { texto: 'La caja está bloqueada por un faltante', to: '/caja' },
    data.stockBajo > 0 && { texto: `${data.stockBajo} producto(s) con existencias bajas`, to: '/inventario' },
    data.aprobacionesPendientes > 0 && { texto: `${data.aprobacionesPendientes} solicitud(es) esperan autorización del dueño`, to: '/aprobaciones' },
  ].filter(Boolean) as { texto: string; to: string }[];

  return (
    <div className="mb-8 grid gap-3">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Indicador etiqueta="Ingresos de hoy" icono={<HandCoins />} valor={formatearMoneda(data.ingresos)} detalle={`${data.ventas} ${data.ventas === 1 ? 'venta cobrada' : 'ventas cobradas'} · neto de devoluciones`} />
        <Indicador
          etiqueta="Citas de hoy"
          icono={<CalendarCheck2 />}
          valor={data.citas.total}
          detalle={`${data.citas.atendidas} ${data.citas.atendidas === 1 ? "atendida" : "atendidas"} · ${data.citas.enServicio} en servicio · ${data.citas.pendientes} por llegar`}
        />
        <Indicador etiqueta="Por cobrar" icono={<Wallet />} valor={formatearMoneda(data.porCobrar.total)} detalle={`${data.porCobrar.ventas} ${data.porCobrar.ventas === 1 ? 'cuenta abierta' : 'cuentas abiertas'}`} />
        <Indicador
          etiqueta="Caja"
          icono={<BellRing />}
          valor={<Insignia tono={CAJA[data.caja].tono} className="px-3 py-1 font-sans text-[15px]">{CAJA[data.caja].texto}</Insignia>}
          detalle={`${data.citas.canceladas} cancelaciones o inasistencias hoy`}
        />
      </div>
      <div className="grid gap-3 lg:grid-cols-[2fr_1fr]">
        <Tarjeta>
          <TarjetaEncabezado titulo="Ingresos de los últimos 7 días" />
          <div className="px-5 pb-4 pt-2">
            <Columnas datos={data.ultimos7.map((d) => ({ etiqueta: diaCorto.format(new Date(`${d.dia}T12:00:00-06:00`)).replace('.', ''), valor: d.total }))} alto={150} />
          </div>
        </Tarjeta>
        <Tarjeta>
          <TarjetaEncabezado titulo="Alertas" />
          <ul className="grid gap-2 px-5 pb-5 pt-1 text-[14px]">
            {alertas.length === 0 ? (
              <li className="text-foreground-muted">Todo en orden por ahora.</li>
            ) : (
              alertas.map((a) => (
                <li key={a.texto}>
                  <Link to={a.to} className="flex items-start gap-2 rounded-md p-1.5 transition-colors hover:bg-muted">
                    {a.to === '/inventario' ? <PackageMinus className="mt-0.5 size-4 shrink-0 text-warning" /> : <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />}
                    {a.texto}
                  </Link>
                </li>
              ))
            )}
          </ul>
        </Tarjeta>
      </div>
    </div>
  );
}
